import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/lib/env';

/**
 * Имя action.type из authSlice. Дублируется здесь намеренно, чтобы
 * не образовывать круговой импорт baseApi → authSlice → authApi → baseApi.
 * Если переименуешь slice — синхронизируй обе константы.
 */
const AUTH_LOGOUT_ACTION = 'auth/logout';

/**
 * Базовый fetcher с httpOnly-cookie. credentials: 'include' обязателен —
 * admin-backend полагается на cookie для access/refresh токенов.
 */
const rawBaseQuery = fetchBaseQuery({
    baseUrl: env.apiUrl,
    credentials: 'include'
});

/**
 * Wrapper над raw-baseQuery с авто-refresh при 401:
 * 1. Если обычный запрос упал с 401 — пробуем POST /admin-auth/refresh.
 * 2. Если refresh успешен — повторяем оригинальный запрос.
 * 3. Если refresh упал — диспатчим logout и возвращаем оригинальную ошибку.
 *
 * Сам endpoint /admin-auth/refresh пропускаем, чтобы не зацикливаться.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    const isRefreshCall =
        typeof args === 'object' && args.url === '/admin-auth/refresh';

    if (result.error?.status === 401 && !isRefreshCall) {
        const refreshResult = await rawBaseQuery(
            { url: '/admin-auth/refresh', method: 'POST' },
            api,
            extraOptions
        );

        if (refreshResult.error) {
            api.dispatch({ type: AUTH_LOGOUT_ACTION });
        } else {
            result = await rawBaseQuery(args, api, extraOptions);
        }
    }

    return result;
};

export const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'AdminMe',
        'Category',
        'Tariff',
        'Moderation',
        'Recipe',
        'Article',
        'Course',
        'Comment',
        'Stream',
        'Event',
        'Subscription',
        'Payment',
        'ProfessionalApplication',
        'CafeApplication',
        'VacancyComplaint',
        'AuditLog',
        'ProfessionalSpecialization',
        'PiiConsentText',
        'CafeStaffSearchLog',
        'IngredientGroup',
        'Ingredient',
        'MenuItemType',
        'CuisineType',
        'City'
    ],
    endpoints: () => ({})
});
